/**
 * @file contractOrders.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description Order lines counted against a contract, on the screen side
 *              (Cam, 28 Oct 2026). The rules are the server's
 *              (utils/contractLinks.js, services/contractLinkService.js);
 *              this only builds requests and words.
 *
 *   A contract screen row stands for a SET of targets: one contract, a
 *   group's products ({ kind: 'contract', ids }), a straights contract's
 *   months ({ kind: 'straights', id }), or a milk powder customer + product's
 *   prices ({ kind: 'milk_powder', ids } — 1.1.0, 28 Oct 2026).
 *
 *   Contract usage counts TONNES (Cam, 28 Oct 2026): each line says what it
 *   counts as (`counts_as`), and why when it counts as typed (`counts_flag`).
 */

/** Query string for a set. */
export function setQuery(set) {
  if (!set) return '';
  if (set.kind === 'straights') return `kind=straights&id=${encodeURIComponent(set.id)}`;
  const ids = (set.ids || []).join(',');
  return `kind=${encodeURIComponent(set.kind)}&ids=${encodeURIComponent(ids)}`;
}

export const setKeyOf = (set) => (set ? `${set.kind}:${set.kind === 'straights' ? set.id : (set.ids || []).join(',')}` : '');

export const targetKey = (t) => (t ? `${t.kind}:${t.id}` : '');

/** "Added by hand" / "Automatic" with who and when. */
export function sourceWords(line, formatDate) {
  if (!line?.source) return '';
  const how = line.source === 'manual' ? 'Added by hand' : 'Automatic';
  const who = line.linked_by ? ` by ${line.linked_by}` : '';
  const when = line.linked_at && formatDate ? ` on ${formatDate(line.linked_at)}` : '';
  return `${how}${who}${when}`;
}

/** Where a line counts now, in words, or ''. */
export function countsNowWords(line) {
  if (line?.link) return `Counts against ${line.link.label}${line.link.source === 'manual' ? ' (added by hand)' : ''}`;
  if (line?.po_counted) return `Counts against ${line.po_counted.label} through ${line.po_counted.po_number || 'its PO'}`;
  return '';
}

/** The default target for each candidate line, by the server's suggestion. */
export function defaultTargets(lines) {
  const out = {};
  for (const l of lines || []) {
    if (l.suggested) out[l.order_line_id] = targetKey(l.suggested);
  }
  return out;
}

/** The request body of an add: [{ order_line_id, kind, target_id }]. */
export function linksFor(selectedIds, chosen, targets) {
  const only = (targets || []).filter(t => !t.is_draft);
  return [...selectedIds].map(id => {
    const key = chosen[id] || (only.length === 1 ? targetKey(only[0]) : '');
    const [kind, targetId] = key.split(':');
    return { order_line_id: Number(id), kind, target_id: Number(targetId) };
  }).filter(x => x.kind && Number.isInteger(x.target_id));
}

/** A refresh's lists, worded for the window. */
export const ACTION_WORDS = {
  link: 'Link',
  move: 'Move',
  convert: 'Now automatic',
  kept: 'Kept',
  skip: 'Left'
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

/**
 * Milk powder's "periods" for Refresh auto-links: calendar months (its prices
 * run From / To, there are no contract periods) — `back` months before this
 * one to `ahead` after, newest first. id 'mp:YYYY-MM'.
 */
export function milkMonthPeriods(today = new Date(), { back = 12, ahead = 2 } = {}) {
  const out = [];
  const y = today.getFullYear();
  const m = today.getMonth();
  for (let i = ahead; i >= -back; i -= 1) {
    const d = new Date(Date.UTC(y, m + i, 1));
    const yy = d.getUTCFullYear();
    const mm = d.getUTCMonth() + 1;
    const last = new Date(Date.UTC(yy, mm, 0)).getUTCDate();
    const ym = `${yy}-${String(mm).padStart(2, '0')}`;
    out.push({ id: `mp:${ym}`, name: `${MONTH_NAMES[mm - 1]} ${yy}`, start_date: `${ym}-01`, end_date: `${ym}-${String(last).padStart(2, '0')}` });
  }
  return out;
}

/** What a set of linked lines comes to, in a line: "3 orders · 4 lines · 2.50 T". */
export function usageWords(usage, formatTonnes = (t) => `${t} T`) {
  if (!usage || !usage.lines) return 'No orders yet';
  const parts = [`${usage.orders} order${usage.orders === 1 ? '' : 's'}`];
  if (usage.lines !== usage.orders) parts.push(`${usage.lines} lines`);
  parts.push(formatTonnes(usage.tonnes));
  return `${parts.join(' · ')}${usage.as_typed ? ` (${usage.as_typed} counted as typed)` : ''}`;
}
